import React, { useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useUserRolePermissions } from 'hooks';
import * as R from 'ramda';
import FilterableDataGrid from 'common/templates/FilterableDataGrid';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import ViewDrawer from '../view-drawer/ViewDrawerConnector';
import EditDrawer from '../edit/EditDrawer';
import EditCustomerAuthorizationDrawer from '../edit-customer-authorization/EditCustomerAuthorizationDrawer';
import {
  fetchCustomerAuthorization,
  setIsViewDrawerOpen,
} from '../store/customerAuthorizationSlice';
import CustomerAuthorizationsGrid from './CustomerAuthorizationsGrid';
import CustomerAuthorizationsFilters from './CustomerAuthorizationsFilters';
import useCustomerAuthFilters from './useCustomerAuthFilters';

const CustomerAuthorizationsListViewPage = () => {
  const { isProductLoadingManage } = useUserRolePermissions();
  const editCustomerAuthorizationDrawerFlag = EvaluateFlag(
    process.env.REACT_APP_LD_EDIT_CUSTOMER_AUTHORIZATION_DRAWER
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [customerAuthorizationId, setCustomerAuthorizationId] = useState(null);
  const [refetchToggle, setRefetchToggle] = useState(false);
  const [drawerMode, setDrawerMode] = useState(null);

  const {
    affiliateDisplayOptions,
    appliedFilters,
    draftFilters,
    handleApply,
    handleReset,
    hasChanges,
    isOnDefaultState,
    locationOptions,
    locationsLoading,
    productOptions,
    productsLoading,
    selectedAffiliate,
    setAffiliateFilter,
    setAuthTypeFilter,
    setControlTypeFilter,
    setLocationFilter,
    setProductFilter,
    setSearch,
    setStatusFilter,
    showAffiliateFilter,
  } = useCustomerAuthFilters();

  const handleNewAuthorization = () => {
    navigate('./create/customer-information');
  };

  const handleEdit = id => {
    if (editCustomerAuthorizationDrawerFlag) {
      setDrawerMode('edit');
    }
    setCustomerAuthorizationId(id);
  };
  const [isExporting, setIsExporting] = useState(false);

  const handleView = id => {
    if (editCustomerAuthorizationDrawerFlag) {
      setDrawerMode('view');
      setCustomerAuthorizationId(id);
      return;
    }
    dispatch(setIsViewDrawerOpen(true));
    dispatch(fetchCustomerAuthorization({ customerAuthorizationId: id }));
  };

  const handleDrawerClose = () => {
    setCustomerAuthorizationId(null);
    setDrawerMode(null);
  };

  return (
    <>
      <FilterableDataGrid
        filterContent={
          <CustomerAuthorizationsFilters
            affiliateOptions={affiliateDisplayOptions}
            authTypes={draftFilters.authTypes}
            controlTypes={draftFilters.controlTypes}
            isExporting={isExporting}
            locationOptions={locationOptions}
            locationsLoading={locationsLoading}
            onAffiliateChange={setAffiliateFilter}
            onAuthTypeChange={setAuthTypeFilter}
            onControlTypeChange={setControlTypeFilter}
            onLocationChange={setLocationFilter}
            onProductChange={setProductFilter}
            onStatusChange={setStatusFilter}
            productOptions={productOptions}
            productsLoading={productsLoading}
            selectedAffiliate={selectedAffiliate}
            selectedLocations={draftFilters.locations}
            selectedProducts={draftFilters.products}
            showAffiliateFilter={showAffiliateFilter}
            status={draftFilters.status}
          />
        }
        handleReset={handleReset}
        hasChanges={hasChanges}
        headerTitle="Customer Authorizations"
        isExporting={isExporting}
        isOnDefaultState={isOnDefaultState}
        onApply={handleApply}
        onSearchChange={setSearch}
        primaryFilterActionButtonLabel="Apply Filters"
        searchValue={draftFilters.search}
        secondaryFilterActionButtonLabel="Reset Filters"
        titleMenu={
          isProductLoadingManage && (
            <Button
              onClick={handleNewAuthorization}
              startIcon={<AddRoundedIcon />}
              variant="vertical-with-icon-contained"
            >
              New Authorization
            </Button>
          )
        }
      >
        <CustomerAuthorizationsGrid
          appliedFilters={appliedFilters}
          isProductLoadingManage={isProductLoadingManage}
          onEdit={handleEdit}
          onExportingChange={setIsExporting}
          onView={handleView}
        />
      </FilterableDataGrid>
      {editCustomerAuthorizationDrawerFlag ? (
        <EditCustomerAuthorizationDrawer
          customerAuthorizationId={customerAuthorizationId}
          mode={drawerMode}
          onEdit={handleEdit}
          setCustomerAuthorizationId={handleDrawerClose}
        />
      ) : (
        <>
          <ViewDrawer setCustomerAuthorizationId={setCustomerAuthorizationId} />
          {R.not(R.isNil(customerAuthorizationId)) && (
            <EditDrawer
              customerAuthorizationId={customerAuthorizationId}
              refetchToggle={refetchToggle}
              setCustomerAuthorizationId={setCustomerAuthorizationId}
              setRefetchToggle={setRefetchToggle}
            />
          )}
        </>
      )}
    </>
  );
};

export default CustomerAuthorizationsListViewPage;
